import { supabase } from './supabase'
import type { Profile, ScoreEvent, Task } from '../types'

export interface CreateTaskInput {
  title: string
  description: string
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL'
  assignedToId: string
  assignedOnBehalfOfId?: string | null
  repeatType?: 'NONE' | 'DAILY' | 'WEEKLY' | 'MONTHLY'
  initialComment?: string
}

export async function getSession() {
  const { data, error } = await supabase.auth.getSession()
  if (error) throw error
  return data.session
}

export async function signInWithGoogle() {
  const { error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: window.location.origin,
      // Access is enforced by the active profile allowlist after OAuth.
      // Do not use Google's hosted-domain hint here because Ashish is an
      // approved organization user whose Google identity is on gmail.com.
      queryParams: { prompt: 'select_account' },
    },
  })
  if (error) throw error
}

export async function signOut() {
  const { error } = await supabase.auth.signOut()
  if (error) throw error
}

export async function getCurrentProfile(): Promise<Profile | null> {
  const session = await getSession()
  if (!session) return null
  const { data, error } = await supabase
    .from('profiles')
    .select('id,auth_user_id,full_name,email,role,team,is_active')
    .eq('auth_user_id', session.user.id)
    .maybeSingle()
  if (error) throw error
  return data as Profile | null
}

export async function getTasks(limit = 250): Promise<Task[]> {
  const { data, error } = await supabase
    .from('tasks')
    .select(`id,task_code,title,description,priority,status,assigned_to_id,assigned_by_id,assigned_date,current_due_date,completion_date,score,score_color,carry_forward_count,archived,created_at,assignee:profiles!tasks_assigned_to_id_fkey(id,full_name,team),assigner:profiles!tasks_assigned_by_id_fkey(id,full_name)`)
    .eq('archived', false)
    .order('assigned_date', { ascending: false })
    .limit(limit)
  if (error) throw error
  return (data ?? []) as unknown as Task[]
}

export async function getAssignableProfiles(): Promise<Profile[]> {
  const { data, error } = await supabase.rpc('get_assignable_profiles')
  if (error) throw error
  return (data ?? []) as Profile[]
}

// Minimal employee directory used only for selecting a help-ticket recipient.
// It deliberately excludes email/auth fields and does not change task visibility.
export async function getHelpProfiles(): Promise<Profile[]> {
  const { data, error } = await supabase.rpc('get_help_profiles')
  if (error) throw error
  return (data ?? []) as Profile[]
}
export async function sendEmail({
  to,
  subject,
  html,
}: {
  to: string | string[]
  subject: string
  html: string
}) {
  const { data, error } = await supabase.functions.invoke('resend-email', {
    body: {
      to,
      subject,
      html,
    },
  })

  if (error) {
    throw new Error(error.message || 'Unable to send email')
  }

  if (data?.success === false) {
    throw new Error(
      typeof data.error === 'string'
        ? data.error
        : JSON.stringify(data.error)
    )
  }

  return data
}

// export async function createTask(input: CreateTaskInput, actor: Profile): Promise<void> {
//   if (!['ADMIN', 'ASSIGNER'].includes(actor.role)) throw new Error('Only Admin or Assigner can create tasks.')

//   const taskId = crypto.randomUUID()

//   const { error: taskError } = await supabase
//     .from('tasks')
//     .insert({
//       id: taskId,
//       title: input.title.trim(),
//       description: input.description.trim() || null,
//       priority: input.priority,
//       status: 'OPEN',
//       assigned_to_id: input.assignedToId,
//       assigned_by_id: actor.id,
//       assigned_date: new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }),
//       archived: false,
//     })
//   if (taskError) throw taskError

//   const { error: assignmentError } = await supabase.from('task_assignments').insert({
//     task_id: taskId,
//     assigned_to_id: input.assignedToId,
//     assigned_by_id: actor.id,
//     is_current: true,
//   })
//   if (assignmentError) throw new Error(`Task created, but assignment history failed: ${assignmentError.message}`)

//   const { error: activityError } = await supabase.from('task_activity').insert({
//     task_id: taskId,
//     event_type: 'TASK_CREATED',
//     actor_id: actor.id,
//     metadata: { assigned_to_id: input.assignedToId, priority: input.priority },
//     notes: input.description.trim() || null,
//   })
//   if (activityError) throw new Error(`Task created, but activity history failed: ${activityError.message}`)
// }
function escapeHtml(value: string) {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;')
}
export async function createTask(
  input: CreateTaskInput,
  actor: Profile
): Promise<void> {
  if (!['ADMIN', 'ASSIGNER'].includes(actor.role)) {
    throw new Error('Only Admin or Assigner can create tasks.')
  }

  const title = input.title.trim()
  if (!title || title.length > 180 || !input.assignedToId) {
    throw new Error('Task title and assignee are required.')
  }

  const { error: taskError } = await supabase.rpc('create_task_secure', {
    p_title: title,
    p_description: input.description.trim() || null,
    p_assigned_to_id: input.assignedToId,
    p_assigned_on_behalf_of_id: input.assignedOnBehalfOfId ?? null,
    p_priority: input.priority,
    p_repeat_type: input.repeatType ?? 'NONE',
    p_initial_comment: input.initialComment?.trim() || null,
  })
  if (taskError) throw taskError

  // 4. Get assigned employee details
  const { data: assignee, error: assigneeError } = await supabase
    .from('profiles')
    .select('id,full_name,email')
    .eq('id', input.assignedToId)
    .maybeSingle()

  if (assigneeError) {
    console.error('Unable to load assignee for email:', assigneeError)
    return
  }

  if (!assignee?.email) {
    console.warn('Task created but assignee has no email address.')
    return
  }

  // 5. Send assignment email
  try {
    await sendEmail({
      to: assignee.email,
      subject: `New Task Assigned: ${input.title.trim()}`,
      html: `
        <div style="font-family:Arial,sans-serif;max-width:650px;margin:auto;color:#1f2937;">
          
          <h2 style="margin-bottom:8px;">
            New Task Assigned
          </h2>

          <p>
            Hello ${escapeHtml(assignee.full_name || 'Team Member')},
          </p>

          <p>
            A new task has been assigned to you in TaskOps.
          </p>

          <table
            style="
              width:100%;
              border-collapse:collapse;
              margin:20px 0;
            "
          >
            <tr>
              <td style="padding:8px;border:1px solid #ddd;"><strong>Task</strong></td>
              <td style="padding:8px;border:1px solid #ddd;">
                ${escapeHtml(input.title.trim())}
              </td>
            </tr>

            <tr>
              <td style="padding:8px;border:1px solid #ddd;"><strong>Priority</strong></td>
              <td style="padding:8px;border:1px solid #ddd;">
                ${escapeHtml(input.priority)}
              </td>
            </tr>

            <tr>
              <td style="padding:8px;border:1px solid #ddd;"><strong>Assigned By</strong></td>
              <td style="padding:8px;border:1px solid #ddd;">
                ${escapeHtml(actor.full_name)}
              </td>
            </tr>

            ${
              input.description.trim()
                ? `
            <tr>
              <td style="padding:8px;border:1px solid #ddd;"><strong>Description</strong></td>
              <td style="padding:8px;border:1px solid #ddd;">
                ${escapeHtml(input.description.trim())}
              </td>
            </tr>
            `
                : ''
            }

          </table>

          <p>
            Please open TaskOps and apply your committed due date.
          </p>

          <div style="margin:18px 0;padding:16px 18px;background:#f8fafc;border:1px solid #e5e7eb;border-radius:8px;">
            <strong>Important: Task scoring rules</strong>
            <p style="margin:8px 0 0;line-height:1.5;color:#4b5563;">
              Your finalized task scores contribute to your calendar-year YTD performance.
            </p>
            <ul style="margin:10px 0 0;padding-left:20px;line-height:1.6;">
              <li><strong>Due-date commitment:</strong> Set your due date within 24 hours of task assignment. If no due date is applied within 24 hours, the task is marked <strong style="color:#dc2626;">Red (0 points)</strong>.</li>
              <li><strong style="color:#059669;">Green (3 points):</strong> Complete and submit the task on or before the committed due date without needing a revision.</li>
              <li><strong style="color:#d97706;">Yellow (1 point):</strong> If the original due date cannot be met, enter the first revised due date on or before the existing due date and complete the task by the revised date.</li>
              <li><strong style="color:#dc2626;">Red (0 points):</strong> The due date crosses without a timely revision, the task is completed after its valid due date, or two or more revisions are used.</li>
              <li><strong>Carry-forward:</strong> When a task becomes Red, the original task remains Red. Its carry-forward is created as a separate new task for the new week and is scored independently.</li>
            </ul>
          </div>

          <p>
            <a
              href="${window.location.origin}"
              style="
                display:inline-block;
                padding:10px 18px;
                background:#111827;
                color:#ffffff;
                text-decoration:none;
                border-radius:6px;
              "
            >
              Open TaskOps
            </a>
          </p>

          <p style="font-size:12px;color:#6b7280;margin-top:25px;">
            This is an automated notification from BrightBots TaskOps.
          </p>

        </div>
      `,
    })
  } catch (emailError) {
    // Do NOT fail task creation just because email failed.
    console.error('Task created but assignment email failed:', emailError)
  }
}  
//     // 4. Send queued assignment notification through Gmail API
//   const { data: emailResult, error: emailError } =
//     await supabase.functions.invoke('resend-email', {
//       body: {
//         task_id: taskId,
//       },
//     })

//   if (emailError || emailResult?.success === false) {
//     console.warn(
//       'Task was created, but assignment email failed:',
//       emailError?.message ||
//         emailResult?.error ||
//         'Unknown email error'
//     )
//   }
// }
export async function getScoreEvents(): Promise<ScoreEvent[]> {
  const { data, error } = await supabase
    .from('task_score_events')
    .select('id,task_id,user_id,week_start,color,points,event_type,effective_date')
    .order('effective_date', { ascending: false })
    .limit(2000)
  if (error) throw error
  return (data ?? []) as ScoreEvent[]
}
export async function getDashboardMetrics(
  from: string | null,
  to: string | null,
  view: string
) {
  const { data, error } = await supabase.rpc('get_dashboard_metrics', {
    p_from: from,
    p_to: to,
    p_view: view,
  })

  if (error) throw error

  return data
} 
// Add this export to the bottom of your CURRENT src/lib/api.ts.
// Do not replace your working email / Gmail Edge Function code with an older api.ts copy.
export async function setTaskDueDate(taskId: string, dueDate: string): Promise<void> {
  const { error } = await supabase
    .from('tasks')
    .update({ current_due_date: dueDate })
    .eq('id', taskId)

  if (error) throw error
}

